import React, { FC, Suspense, useCallback, useEffect, useState } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';

import { useBrandedNavigate } from './hooks/useBrandedNavigate/useBrandedNavigate';
import './scss/index.scss';
import { APP_URLS } from './consts';
import { LayoutInit } from './components/LayoutInit';
import { sessionPreviousLocation } from './utils/storage';
import { Snackbar } from './components/shared/Snackbar';
import { Spinner } from './components/shared/Spinner';
import { ScrollToTop } from './utils/scroll-to-top';
import ErrorPage from './components/Navigation/ErrorPage/ErrorPage';
import { ProvidersCombiner } from './providers';
import { APP_VERSION } from './version';
import { CookieBanner } from './components/Compliance/CookieBanner';
import { handleMarketingCookie } from './utils/marketing-preferences-helper';
import { NativeEventBus } from './native/NativeEventBus';
import { BRANDS } from './components/Itv/types';
import { SEO } from './components/seo/SEO';
import { useQueryParams } from './hooks/routing';

// Route-level code splitting — pages load on demand, main routes prefetched on idle
const ItvMain = React.lazy(() => import('./layouts/pages/ItvMain').then((m) => ({ default: m.ItvMain })));
const BingoPage = React.lazy(() => import('./layouts/pages/BingoPage').then((m) => ({ default: m.BingoPage })));
const SpinsPage = React.lazy(() => import('./layouts/pages/SpinsPage').then((m) => ({ default: m.SpinsPage })));
const PrizesPage = React.lazy(() => import('./layouts/pages/PrizesPage').then((m) => ({ default: m.PrizesPage })));
const ItvStaticLanding = React.lazy(() =>
  import('./layouts/pages/ItvStaticLanding').then((m) => ({ default: m.ItvStaticLanding }))
);
const ItvGameDetailsPage = React.lazy(() =>
  import('./layouts/pages/ItvGameDetailsPage/ItvGameDetailsPage').then((m) => ({
    default: m.ItvGameDetailsPage,
  }))
);
const ItvBingoDetailsPage = React.lazy(() =>
  import('./layouts/pages/ItvBingoDetailsPage/ItvBingoDetailsPage').then((m) => ({
    default: m.ItvBingoDetailsPage,
  }))
);
const ItvPromo = React.lazy(() => import('./components/Itv/ItvPromo').then((m) => ({ default: m.ItvPromo })));
const ContentPage = React.lazy(() => import('./layouts/pages/ContentPage').then((m) => ({ default: m.ContentPage })));
const IframePage = React.lazy(() =>
  import('./layouts/pages/IframePage/IframePage').then((m) => ({ default: m.IframePage }))
);
const MinigamesPage = React.lazy(() =>
  import('./layouts/pages/MinigamesPage/MinigamesPage').then((m) => ({ default: m.MinigamesPage }))
);
const SurveyPage = React.lazy(() =>
  import('./layouts/pages/SurveyPage/SurveyPage').then((m) => ({ default: m.SurveyPage }))
);
const FtpPage = React.lazy(() => import('./layouts/pages/FtpPage/FtpPage').then((m) => ({ default: m.FtpPage })));
const Registration = React.lazy(() => import('./components/Registration').then((m) => ({ default: m.Registration })));
const Login = React.lazy(() => import('./components/Login/Login').then((m) => ({ default: m.Login })));

const AuthenticatedOverlays = React.lazy(() => import('./components/Auth/AuthenticatedOverlays'));

export const App: FC = () => {
  const auth = useAuth();
  const location = useLocation();

  const { navigate, navigateHome } = useBrandedNavigate();
  const isUserAuthenticated = auth?.isAuthenticated;
  const [accountOpen, setAccountOpen] = useState<boolean>(false);
  const storedPreviousLocation = sessionPreviousLocation.get() as { previousLocation: string };
  const [prefLocation, storeLocation] = [location.state?.previousLocation, storedPreviousLocation?.previousLocation];
  const previousLocation = accountOpen ? storeLocation || prefLocation : prefLocation || storeLocation;

  ScrollToTop();

  const handleAccountClose = useCallback(
    (isNestedRoute?: boolean): void => {
      setAccountOpen(false);

      if (isNestedRoute) {
        if (previousLocation) {
          navigate(previousLocation);
        } else {
          navigateHome();
        }
      } else {
        navigate(-1);
      }

      sessionPreviousLocation.remove();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate, navigateHome, previousLocation]
  );

  const handleAccountOpen = (): void => {
    // TODO: Refresh wallet totals
    setAccountOpen(true);
  };

  useEffect(() => {
    handleMarketingCookie();

    window.REACT_APP_VERSION = APP_VERSION || 'dev1';

    if (!NativeEventBus.isNativeBridgeAvailable()) {
      const script = document.createElement('script');

      script.id = 'xtremepush-script';
      script.type = 'text/javascript';
      script.innerHTML = `
        (function(p,u,s,h,e,r,l,i,b) {p['XtremePushObject']=s;p[s]=function(){
          (p[s].q=p[s].q||[]).push(arguments)};i=u.createElement('script');i.async=1;
          i.src=h;b=u.getElementsByTagName('script')[0];b.parentNode.insertBefore(i,b);
        })(window,document,'xtremepush','${import.meta.env.VITE_XP_SDK_URL}');
      `;
      document.body.appendChild(script);
    }

    // Prefetch the most-visited chunks while the browser is idle so returning
    // users never see a spinner on navigation to bingo/spins or game details.
    const prefetch = (): void => {
      import('./layouts/pages/ItvMain');
      import('./layouts/pages/BingoPage');
      import('./layouts/pages/ItvGameDetailsPage/ItvGameDetailsPage');
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(prefetch);
    } else {
      setTimeout(prefetch, 1000);
    }
  }, []);

  useEffect(() => {
    const hasAccountQuery = Boolean(location.pathname.includes(APP_URLS.myAccount.home));

    if (isUserAuthenticated) {
      if (hasAccountQuery && !accountOpen) {
        // Open account modal when navigating to account URL
        handleAccountOpen();
      } else if (!hasAccountQuery && accountOpen) {
        // Close account modal when navigating away from account URL
        setAccountOpen(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isUserAuthenticated, accountOpen]);
  const { getQueryParam } = useQueryParams();
  const navigatingToGameInMiniMode = getQueryParam('minigame');

  return (
    <>
      <ProvidersCombiner>
        <SEO canonicalPath={location.pathname} />
        <div id="gameCardZoomPortal" />
        <LayoutInit />
        <Snackbar />
        <Suspense fallback={<Spinner />}>
          <Routes location={previousLocation || location}>
            <Route path={APP_URLS.itv.landing} element={<ItvStaticLanding />}></Route>
            {navigatingToGameInMiniMode && <Route path={APP_URLS.itv.gameDetails} element={<ItvGameDetailsPage />} />}

            <Route path={APP_URLS.itv.root} element={<ItvMain />}>
              <Route path={APP_URLS.itv.spinsSubPages} element={<SpinsPage />}></Route>
              <Route path={APP_URLS.itv.prizesSubPages} element={<PrizesPage />}></Route>
              <Route path={APP_URLS.itv.bingoSubPages} element={<BingoPage />}></Route>
              <Route path={APP_URLS.itv.gameDetails} element={<ItvGameDetailsPage />} />
              <Route path={APP_URLS.itv.bingoDetails} element={<ItvBingoDetailsPage />} />

              <Route path={APP_URLS.itv.prizesSubPages} element={<PrizesPage />}></Route>
              <Route path={APP_URLS.itv.bingo} element={<BingoPage />}></Route>
              <Route path={APP_URLS.itv.bingoSubPages} element={<BingoPage />}></Route>
              <Route path={APP_URLS.itv.spins} element={<SpinsPage />}></Route>
              <Route path={APP_URLS.itv.spinsSubPages} element={<SpinsPage />}></Route>
              <Route path={APP_URLS.itv.promotion} element={<ItvPromo />} />
              <Route path={APP_URLS.itv.contentPage} element={<ContentPage />} />
              <Route path={APP_URLS.itv.gam} element={<IframePage />} />
              <Route path={APP_URLS.ftp} element={<FtpPage />} />
            </Route>

            {!isUserAuthenticated && (
              <>
                <Route path={APP_URLS.itv.bingoRegistration} index element={<Registration variant={BRANDS.BINGO} />} />
                <Route path={APP_URLS.itv.spinsRegistration} index element={<Registration variant={BRANDS.SPINS} />} />
              </>
            )}
            <Route path={APP_URLS.contentPage} element={<ContentPage />} />
            <Route path={APP_URLS.gam} element={<IframePage />} />
            <Route path={APP_URLS.minigames} element={<MinigamesPage />} />
            <Route path={APP_URLS.survey} element={<SurveyPage />} />
            {!isUserAuthenticated && <Route path={APP_URLS.login} element={<Login />} />}
            {previousLocation ? null : <Route path="*" element={<ErrorPage />} />}
          </Routes>
        </Suspense>

        {isUserAuthenticated ? (
          <Suspense fallback={null}>
            <AuthenticatedOverlays
              pathname={location.pathname}
              isMiniMode={navigatingToGameInMiniMode === 'true'}
              accountOpen={accountOpen}
              onAccountClose={handleAccountClose}
            />
          </Suspense>
        ) : (
          location.pathname !== APP_URLS.minigames && navigatingToGameInMiniMode !== 'true' && <CookieBanner />
        )}
      </ProvidersCombiner>
    </>
  );
};
